import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';
import VisibilitySensor from 'react-visibility-sensor';
import { observer } from 'mobx-react-lite';
import translations from '../../constants/translations';
import useStores from '../../stores/useStores';
import DataGrid from '../dataGrid/DataGrid';
import DataSection from '../dataSection/DataSection';
import QuickMenu from '../quickMenu/QuickMenu';
import UseChart from './UseChart';
import { Spinner } from '..';
import BuildingRecommendations from './BuildingRecommendations';
import Chart from './Chart';
import Contact from './Contact';
import Place from './Place';
import PowerlineMap from './PowerlineMap';
import EnergyRatingIcons from './EnergyRatingIcons';
import Financials from '../financials/Financials';
import LabelSwitch from '../general/LabelSwitch';

function BuildingDetails({
  isDemo,
  isVendor,
  building,
  projectId,
  buildingId,
  isAdmin,
  detailsContainerRef
}) {
  const { descriptions } = translations;
  const { buildingDetailStore } = useStores();
  const history = useHistory();
  const [isLazyLoading, setIsLazyLoading] = useState(false);
  const [quickMenuAnchor, setQuickMenuAnchor] = useState(null);

  const {
    location: { hash }
  } = history;
  const activeHash = !isLazyLoading && hash && hash.replace('#', '');

  const {
    details,
    sections,
    quickMenuLinks,
    places,
    power_line,
    temperature,
    estimates,
    weather_timestamp,
    wind_speed,
    building: buildingData
  } = buildingDetailStore;

  const visibleSections = [...sections].filter(section => {
    if (isVendor && section.id === 'load_curve') return false;
    return true;
  });

  const handleLazyLoad = key => {
    const promises = [];
    const { lon, lat } = building.centroid;
    if ((!key || key === 'places') && !places) {
      promises.push(buildingDetailStore.getPlaces(buildingId, lon, lat));
    }
    if (
      (!key || ['closest_power_plant', 'power_line'].includes(key)) &&
      !power_line
    ) {
      promises.push(buildingDetailStore.getPowerOptions(buildingId, lon, lat));
    }
    if ((!key || ['temperature', 'wind_speed'].includes(key)) && !temperature) {
      promises.push(Promise.resolve());
    }
    return Promise.all(promises);
  };

  useEffect(() => {
    if (hash && building && visibleSections.length) {
      // setQuickMenuAnchor(null);
      (async () => {
        setIsLazyLoading(true);
        await handleLazyLoad();
        setIsLazyLoading(false);
      })();
    }
  }, [building, hash, visibleSections]);

  const onVisibilityChange = async (isVisible, id) => {
    if (isVisible) {
      await handleLazyLoad(id);
    }
  };

  if (!building || !visibleSections.length) {
    return (
      <Spinner style={{ marginTop: '50vh', transform: 'translateY(-50%)' }} />
    );
  }

  const {
    address,
    centroid,
    chp,
    contacts,
    electric_utility_short,
    electricity_forecast,
    fuel_cell,
    hourly_predictions,
    icons,
    monthly_predictions,
    predictionsTrace,
    pv,
    pvwattsTrace,
    reductions,
    solar_thermal,
    storage,
    tariff,
    therm_production,
    formatted_details
  } = building;

  if (predictionsTrace && predictionsTrace.length) {
    translations.predictions = 'Predicted Load Profile';
  }

  const { lon, lat } = building.centroid;

  const showUploadedData = isAdmin && buildingDetailStore.showUploadedData;
  return (
    <>
      <div className="building--details">
        {showUploadedData && (
          <Grid item xs={12}>
            <DataSection setQuickMenuAnchor={setQuickMenuAnchor} title="DATA">
              <DataGrid data={formatted_details} />
            </DataSection>
          </Grid>
        )}

        <div className="data-section data-section-recommendations">
          <div className="data-section-content">
            {isVendor ? (
              <>
                {(icons.energystar || icons.eui) &&
                  icons.energystar !== 'n/a' && (
                    <EnergyRatingIcons nowrap icons={icons} />
                  )}
              </>
            ) : (
              <BuildingRecommendations
                chp={chp}
                descriptions={descriptions}
                estimates={estimates}
                fuel_cell={fuel_cell}
                icons={icons}
                pv={pv}
                reductions={reductions}
                solar_thermal={solar_thermal}
                storage={storage}
                therm_production={therm_production}
              />
            )}
          </div>
        </div>

        {isDemo && (
          <>
            <DataSection
              id="load_curve"
              loading={false}
              setQuickMenuAnchor={setQuickMenuAnchor}
              title="Load Curve"
            >
              <Chart
                chartType="load_curve"
                showMenu={isAdmin}
                electric_utility_short={electric_utility_short}
                electricity_forecast={electricity_forecast}
                hourly_predictions={hourly_predictions}
                monthly_predictions={monthly_predictions}
                predictionsTrace={predictionsTrace}
                pvwattsTrace={pvwattsTrace}
                temperature={temperature}
                wind_speed={wind_speed}
                weather_timestamp={weather_timestamp}
                latitude={lat}
                longitude={lon}
                buildingId={buildingId}
                projectId={projectId}
              />
            </DataSection>
          </>
        )}

        {projectId && (
          <DataSection
            id="financials"
            loading={false}
            setQuickMenuAnchor={setQuickMenuAnchor}
            title="Financials"
            secondaryAction={
              buildingDetailStore.financials?.formatted_financials_ppa ? (
                <LabelSwitch
                  options={[
                    {
                      id: 'capex',
                      label: 'Capex'
                    },
                    {
                      id: 'ppa',
                      label: 'PPA'
                    }
                  ]}
                  onChange={(event, value) => {
                    buildingDetailStore.setFinancialMode(value.id);
                  }}
                />
              ) : null
            }
          >
            <Financials
              disablePadding
              projectId={projectId}
              buildingId={buildingId}
              isVendor={isVendor}
              isAdmin={isAdmin}
            />
          </DataSection>
        )}

        {visibleSections
          .filter(section => {
            if (isDemo || projectId) return section.id !== 'load_curve';
            return true;
          })
          .map(section => {
            const { id, title } = section;
            return (
              <VisibilitySensor
                delayedCall
                containment={detailsContainerRef.current}
                partialVisibility
                offset={{ top: 200 }}
                key={id}
                active={[
                  'closest_power_plant',
                  'places',
                  'power_line',
                  'temperature',
                  'wind_speed'
                ].includes(id)}
                onChange={isVisible => onVisibilityChange(isVisible, id)}
              >
                <DataSection
                  id={id}
                  isActive={id === activeHash}
                  loading={
                    ['closest_power_plant', 'places', 'power_line'].includes(
                      id
                    ) && !buildingDetailStore[id]
                  }
                  setQuickMenuAnchor={setQuickMenuAnchor}
                  title={title}
                >
                  {[
                    'predictions',
                    'load_curve',
                    'wind_speed',
                    'temperature',
                    'electricity_forecast'
                  ].includes(id) ? (
                    <Chart
                      chartType={id}
                      showMenu={isAdmin}
                      electric_utility_short={electric_utility_short}
                      electricity_forecast={electricity_forecast}
                      hourly_predictions={hourly_predictions}
                      monthly_predictions={monthly_predictions}
                      predictionsTrace={predictionsTrace}
                      pvwattsTrace={pvwattsTrace}
                      temperature={temperature}
                      wind_speed={wind_speed}
                      weather_timestamp={weather_timestamp}
                      latitude={lat}
                      longitude={lon}
                      buildingId={buildingId}
                      projectId={projectId}
                    />
                  ) : (
                    <>
                      {id !== 'energy_use' && <DataGrid data={details[id]} />}
                    </>
                  )}
                  {id === 'power_line' && (
                    <PowerlineMap
                      address={address}
                      building={building}
                      buildingId={buildingId}
                      centroid={centroid}
                      power_line={power_line}
                    />
                  )}
                  {id === 'energy_use' && buildingData?.metrics && (
                    <UseChart
                      data={details.energy_use}
                      cbecsData={buildingData.cbecs}
                      metrics={buildingData.metrics}
                    />
                  )}
                </DataSection>
              </VisibilitySensor>
            );
          })}

        {tariff && (
          <DataSection
            id="tariff"
            isActive={activeHash === 'tariff'}
            setQuickMenuAnchor={setQuickMenuAnchor}
            title={`Tariff: ${tariff.name}`}
          >
            <table width="100%">
              <tbody>
                {tariff.data.map(col => (
                  <tr>
                    <td>{col[0]}</td>
                    <td>{col[1]}</td>
                    <td>{col[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataSection>
        )}
        <VisibilitySensor
          delayedCall
          containment={detailsContainerRef.current}
          partialVisibility
          offset={{ top: 200 }}
          onChange={isVisible => onVisibilityChange(isVisible, 'places')}
        >
          <DataSection
            id="places"
            isActive={activeHash === 'places'}
            setQuickMenuAnchor={setQuickMenuAnchor}
            title="Places"
          >
            <Grid container spacing={2}>
              {places &&
                places.map(place => (
                  <Grid item xs={12} md={6} lg={4} key={place.id}>
                    <Place {...place} />
                  </Grid>
                ))}
            </Grid>
          </DataSection>
        </VisibilitySensor>
        {contacts && !!contacts.length && (
          <DataSection
            id="contacts"
            isActive={activeHash === 'contacts'}
            setQuickMenuAnchor={setQuickMenuAnchor}
            title="Contacts"
          >
            <Grid container spacing={2}>
              {contacts.map(contact => (
                <Grid item xs={12} md={6} lg={4} key={contact.name}>
                  <Contact {...contact} />
                </Grid>
              ))}
            </Grid>
          </DataSection>
        )}
      </div>
      <QuickMenu
        anchorEl={quickMenuAnchor}
        links={quickMenuLinks}
        onClose={() => setQuickMenuAnchor(null)}
        onExit={() => setQuickMenuAnchor(null)}
      />
    </>
  );
}

export default observer(BuildingDetails);
